import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QualificationRule, LOCATION_TYPES, LocationType, RuleOutcome } from '../../types/qualificationRule';
import { qualificationRulesApi } from '../../api/qualificationRules.api';
import { toast } from '../../utils/toast';

interface QualificationRulesMatrixProps {
  companyId: string | null;
  isSuperAdmin: boolean;
}

// ─── Display helpers ──────────────────────────────────────────────────────────

const LOCATION_LABELS: Record<LocationType, string> = {
  domicile:   '🏠 Domicile',
  travail:    '💼 Travail habituel',
  client:     '🤝 Client / mission',
  lieu_public:'📍 Autre pro',
  autre:      '❓ Autre perso',
};

const LOCATION_SUBLABELS: Record<LocationType, string> = {
  domicile:   'Votre résidence',
  travail:    'Votre site habituel',
  client:     'Intervention ou RDV',
  lieu_public:'Lieu lié au travail',
  autre:      'Lieu privé',
};

const OUTCOME_LABELS: Record<RuleOutcome, string> = {
  professional: 'Professionnel',
  personal: 'Personnel',
  ask_driver: 'Chauffeur décide',
};

// ─── Chip component ───────────────────────────────────────────────────────────

interface ChipProps {
  outcome: RuleOutcome | null;
  isGlobal?: boolean;
}

const OutcomeChip: React.FC<ChipProps> = ({ outcome, isGlobal }) => {
  if (outcome === null) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-400">
        –
      </span>
    );
  }

  const styles: Record<RuleOutcome, string> = {
    professional: 'bg-green-100 text-green-700',
    personal: 'bg-orange-100 text-orange-700',
    ask_driver: 'bg-blue-100 text-blue-700',
  };

  const labels: Record<RuleOutcome, string> = {
    professional: 'PRO',
    personal: 'PERSO',
    ask_driver: 'Chauffeur',
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${styles[outcome]}`}>
      {labels[outcome]}
      {isGlobal && (
        <span className="text-[10px] font-normal opacity-70">(G)</span>
      )}
    </span>
  );
};

// ─── Popover for cell editing ─────────────────────────────────────────────────

interface CellPopoverProps {
  currentOutcome: RuleOutcome | null;
  hasRule: boolean;
  onSelect: (outcome: RuleOutcome | null) => void;
  onClose: () => void;
}

const CellPopover: React.FC<CellPopoverProps> = ({ currentOutcome, hasRule, onSelect, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const options: { label: string; value: RuleOutcome; color: string }[] = [
    { label: 'Professionnel', value: 'professional', color: 'text-green-700 hover:bg-green-50' },
    { label: 'Personnel', value: 'personal', color: 'text-orange-700 hover:bg-orange-50' },
    { label: 'Chauffeur décide', value: 'ask_driver', color: 'text-blue-700 hover:bg-blue-50' },
  ];

  return (
    <div
      ref={ref}
      className="absolute z-20 top-full left-1/2 -translate-x-1/2 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1"
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          className={`w-full text-left px-3 py-2 text-sm font-medium transition-colors ${opt.color} ${
            currentOutcome === opt.value ? 'opacity-50 cursor-default' : ''
          }`}
          disabled={currentOutcome === opt.value}
        >
          {opt.label}
          {currentOutcome === opt.value && (
            <span className="ml-1 text-xs font-normal opacity-60">(actuel)</span>
          )}
        </button>
      ))}
      {hasRule && (
        <>
          <div className="my-1 border-t border-gray-100" />
          <button
            onClick={() => onSelect(null)}
            className="w-full text-left px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
          >
            Supprimer la règle
          </button>
        </>
      )}
    </div>
  );
};

// ─── Single matrix cell ───────────────────────────────────────────────────────

interface CellProps {
  rule: QualificationRule | undefined;
  departureType: LocationType;
  arrivalType: LocationType;
  companyId: string | null;
  isSuperAdmin: boolean;
  onRuleChange: () => void;
}

const MatrixCell: React.FC<CellProps> = ({
  rule,
  departureType,
  arrivalType,
  companyId,
  isSuperAdmin,
  onRuleChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSelect = useCallback(
    async (outcome: RuleOutcome | null) => {
      setIsOpen(false);
      setIsLoading(true);
      try {
        if (outcome === null) {
          // Delete the rule
          if (rule?._id) {
            await qualificationRulesApi.deleteRule(rule._id);
            toast.success('Règle supprimée');
          }
        } else if (rule && rule._id && (!companyId || rule.source === 'company')) {
          // Update existing rule:
          //   – global mode (companyId=null): update the global rule directly
          //   – company mode: update the company-specific rule
          await qualificationRulesApi.updateRule(rule._id, { outcome });
          toast.success('Règle mise à jour');
        } else {
          // Create a new rule.
          // In company mode with an inherited global rule, this creates a company-level override.
          // In global mode (companyId=null), this creates a new global rule.
          await qualificationRulesApi.createRule({
            company_id: companyId,
            departure_type: departureType,
            arrival_type: arrivalType,
            outcome,
          });
          toast.success('Règle créée');
        }
        onRuleChange();
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Erreur lors de la modification de la règle');
      } finally {
        setIsLoading(false);
      }
    },
    [rule, companyId, departureType, arrivalType, onRuleChange]
  );

  const outcome = rule?.outcome ?? null;
  const isGlobal = rule?.source === 'global';
  const hasRule = !!rule;

  return (
    <td className="relative px-3 py-3 text-center whitespace-nowrap border border-gray-100">
      {isLoading ? (
        <div className="flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : isSuperAdmin ? (
        <div className="relative inline-block">
          <button
            onClick={() => setIsOpen((v) => !v)}
            className="hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-1 rounded"
            title={`${LOCATION_LABELS[departureType]} → ${LOCATION_LABELS[arrivalType]}`}
          >
            <OutcomeChip outcome={outcome} isGlobal={isGlobal} />
          </button>
          {isOpen && (
            <CellPopover
              currentOutcome={outcome}
              hasRule={hasRule}
              onSelect={handleSelect}
              onClose={() => setIsOpen(false)}
            />
          )}
        </div>
      ) : (
        <OutcomeChip outcome={outcome} isGlobal={isGlobal} />
      )}
    </td>
  );
};

// ─── Main matrix component ────────────────────────────────────────────────────

export const QualificationRulesMatrix: React.FC<QualificationRulesMatrixProps> = ({
  companyId,
  isSuperAdmin,
}) => {
  const [rules, setRules] = useState<QualificationRule[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRules = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Global mode (companyId=null): fetch global rules directly.
      // Company mode: fetch the merged effective ruleset (global base + company overrides).
      const response = companyId
        ? await qualificationRulesApi.getEffectiveRuleset(companyId)
        : await qualificationRulesApi.getRules(null);
      setRules(response.data.rules);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des règles');
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  // Build a lookup map: "departure_type:arrival_type" → QualificationRule
  const ruleMap = new Map<string, QualificationRule>();
  for (const rule of rules) {
    ruleMap.set(`${rule.departure_type}:${rule.arrival_type}`, rule);
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-10 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-6 text-center">
        <p className="text-red-600 text-sm">{error}</p>
        <button
          onClick={loadRules}
          className="mt-3 text-sm text-primary-600 hover:underline"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              {/* Top-left corner label */}
              <th className="px-4 py-3 text-left border border-gray-200">
                <div className="text-xs font-semibold text-gray-500 leading-tight">
                  <div>Départ</div>
                  <div className="text-gray-400 font-normal">→ Arrivée</div>
                </div>
              </th>
              {LOCATION_TYPES.map((arrType) => (
                <th
                  key={arrType}
                  className="px-3 py-3 text-center border border-gray-200 whitespace-nowrap"
                >
                  <div className="text-xs font-semibold text-gray-700">{LOCATION_LABELS[arrType]}</div>
                  <div className="text-[11px] font-normal text-gray-400 mt-0.5">{LOCATION_SUBLABELS[arrType]}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {LOCATION_TYPES.map((depType) => (
              <tr key={depType} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 border border-gray-200 whitespace-nowrap bg-gray-50">
                  <div className="text-xs font-semibold text-gray-700">{LOCATION_LABELS[depType]}</div>
                  <div className="text-[11px] font-normal text-gray-400 mt-0.5">{LOCATION_SUBLABELS[depType]}</div>
                </td>
                {LOCATION_TYPES.map((arrType) => (
                  <MatrixCell
                    key={`${depType}:${arrType}`}
                    rule={ruleMap.get(`${depType}:${arrType}`)}
                    departureType={depType}
                    arrivalType={arrType}
                    companyId={companyId}
                    isSuperAdmin={isSuperAdmin}
                    onRuleChange={loadRules}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isSuperAdmin && (
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
          Cliquez sur une cellule pour modifier la règle. <span className="font-medium">(G)</span> = règle globale par défaut.
        </div>
      )}
    </div>
  );
};

export { OUTCOME_LABELS };
